/* eslint-disable react/jsx-props-no-spreading */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Tooltip } from 'uikit/tooltips';
import { Typo } from 'uikit/content';
import { createIconComponent } from 'uikit/icons';

import styles from '../styles/uikitMetaMeta.module.scss';

const Meta = ({
  children,
  className = '',
  element = 'ul',
  hasDividers = false,
  isCompact = false,
  isNoWrap = false,
  testId = '',
}) => {
  const Element = element;
  return (
    <Element
      className={classnames(
        styles.element,
        { [styles.isNoWrap]: isNoWrap },
        { [styles.isCompact]: isCompact },
        { [styles.hasDividers]: hasDividers },
        className,
      )}
      data-testid={testId}
    >
      {children}
    </Element>
  );
};

const Item = ({
  className = '',
  element = 'li',
  icon = '',
  iconA11yText = '',
  iconSize = 18,
  iconTooltipText = '',
  isNoWrap = false,
  keyName = '',
  testId = '',
  text = '',
  variant = 's01',
  modifier = '',
  weight = '',
}) => {
  const Element = element;
  const IconComponent = createIconComponent({
    icon,
    className: styles.icon,
    size: iconSize,
    'aria-hidden': true,
  });
  const IconWrapper = iconTooltipText ? Tooltip : React.Fragment;
  const iconWrapperProps = {};
  if (iconTooltipText) {
    iconWrapperProps.content = iconTooltipText;
    if (iconTooltipText === iconA11yText) {
      iconWrapperProps.aria = { content: 'labelledby' };
    }
  }

  const getContent = () => {
    const typo = (
      <Typo
        element="span"
        color="secondary"
        modifier={modifier}
        noWrap={isNoWrap}
        weight={weight}
        variant={variant}
      >
        {text}
      </Typo>
    );
    if (isNoWrap) {
      return <Tooltip content={text}>{typo}</Tooltip>;
    }
    return typo;
  };

  return (
    <Element
      className={classnames(styles.item, { [styles.isNoWrap]: isNoWrap }, className)}
      data-testid={testId}
      data-key={keyName}
      key={keyName}
    >
      {icon && iconA11yText && iconA11yText !== iconTooltipText && (
        <span className={styles.iconAccessibilityText}>{iconA11yText}</span>
      )}
      {icon && <IconWrapper {...iconWrapperProps}>{IconComponent}</IconWrapper>}
      {text && getContent()}
    </Element>
  );
};

Meta.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  element: PropTypes.string,
  hasDividers: PropTypes.bool,
  isCompact: PropTypes.bool,
  isNoWrap: PropTypes.bool,
  testId: PropTypes.string,
};

Item.propTypes = {
  className: PropTypes.string,
  element: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  iconA11yText: PropTypes.string,
  iconSize: PropTypes.number,
  iconTooltipText: PropTypes.string,
  isNoWrap: PropTypes.bool,
  keyName: PropTypes.string,
  modifier: PropTypes.string,
  testId: PropTypes.string,
  text: PropTypes.string,
  variant: PropTypes.string,
  weight: PropTypes.string,
};

Meta.Item = memo(Item);

export default Meta;
